/**
 * FloorPlanManager — visual floor plan editor for table-service venues.
 *
 * Tables live on an abstract 100×100 grid (FLOOR_GRID_SIZE); the POS renders
 * the same coordinates, so what you lay out here is what cashiers see.
 * Drag to move; click to select and edit name/seats/shape/size/room.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { DEFAULT_ROOM, FLOOR_GRID_SIZE } from '@cafe-pos/shared';
import { supabase } from '../supabase.js';
import { Trash2, Users } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type TableRow = Database['public']['Tables']['restaurant_tables']['Row'];

const GRID_SNAP = 1;
const MIN_SIZE = 6;
const MAX_SIZE = 40;

function snap(v: number): number {
  return Math.round(v / GRID_SNAP) * GRID_SNAP;
}

function clampPos(v: number, size: number): number {
  return Math.min(Math.max(v, 0), FLOOR_GRID_SIZE - size);
}

/** First free "T<n>" name in the room. */
function nextTableName(rows: TableRow[], room: string): string {
  const names = new Set(rows.filter((r) => r.room === room).map((r) => r.name));
  for (let i = 1; i < 200; i++) {
    if (!names.has(`T${i}`)) return `T${i}`;
  }
  return `T${rows.length + 1}`;
}

export function FloorPlanManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Rooms created in this session that have no tables yet. */
  const [draftRooms, setDraftRooms] = useState<string[]>([]);
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<{
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('restaurant_tables')
      .select('*')
      .order('room', { ascending: true })
      .order('name', { ascending: true });
    if (err) setError(err.message);
    else setRows((data ?? []) as TableRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const rooms = useMemo(() => {
    const fromRows = [...new Set(rows.map((r) => r.room))];
    const extra = draftRooms.filter((r) => !fromRows.includes(r));
    const all = [...fromRows, ...extra];
    return all.length > 0 ? all : [DEFAULT_ROOM];
  }, [rows, draftRooms]);

  const effectiveRoom = activeRoom && rooms.includes(activeRoom) ? activeRoom : rooms[0]!;
  const roomTables = rows.filter((r) => r.room === effectiveRoom);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  // ------------------------------------------------------------- persistence

  async function patchTable(id: string, patch: Partial<TableRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    if (!supabase) return;
    const { error: err } = await supabase
      .from('restaurant_tables')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      void load();
    }
  }

  async function addTable() {
    if (!supabase || !clientId || saving) return;
    setSaving(true);
    setError(null);
    // Stagger new tables so they don't stack on one spot.
    const count = roomTables.length;
    const insert: Database['public']['Tables']['restaurant_tables']['Insert'] = {
      client_id: clientId,
      room: effectiveRoom,
      name: nextTableName(rows, effectiveRoom),
      seats: 2,
      shape: 'square',
      pos_x: clampPos(5 + (count % 6) * 15, 12),
      pos_y: clampPos(5 + Math.floor(count / 6) * 18, 12),
      width: 12,
      height: 12,
    };
    const { data, error: err } = await supabase
      .from('restaurant_tables')
      .insert(insert)
      .select('*')
      .single();
    if (err) setError(err.message);
    else if (data) {
      setRows((prev) => [...prev, data as TableRow]);
      setSelectedId((data as TableRow).id);
    }
    setSaving(false);
  }

  async function deleteTable(id: string) {
    if (!supabase) return;
    setError(null);
    const { error: err } = await supabase.from('restaurant_tables').delete().eq('id', id);
    if (err) {
      // FK from historical orders — deactivate instead of hard delete.
      await patchTable(id, { active: false });
      setError(t('floorPlan.deactivatedInstead'));
    } else {
      setRows((prev) => prev.filter((r) => r.id !== id));
    }
    setSelectedId(null);
  }

  function addRoom() {
    const name = window.prompt(t('floorPlan.roomNamePrompt'));
    const trimmed = name?.trim();
    if (!trimmed) return;
    if (!rooms.includes(trimmed)) setDraftRooms((prev) => [...prev, trimmed]);
    setActiveRoom(trimmed);
    setSelectedId(null);
  }

  // ------------------------------------------------------------------- drag

  function onTablePointerDown(e: React.PointerEvent, row: TableRow) {
    if (!canvasRef.current) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = {
      id: row.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: Number(row.pos_x),
      origY: Number(row.pos_y),
      moved: false,
    };
  }

  function onTablePointerMove(e: React.PointerEvent) {
    const drag = dragState.current;
    if (!drag || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.startX) / rect.width) * FLOOR_GRID_SIZE;
    const dy = ((e.clientY - drag.startY) / rect.height) * FLOOR_GRID_SIZE;
    if (!drag.moved && Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
    drag.moved = true;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== drag.id) return r;
        return {
          ...r,
          pos_x: clampPos(snap(drag.origX + dx), Number(r.width)),
          pos_y: clampPos(snap(drag.origY + dy), Number(r.height)),
        };
      }),
    );
  }

  function onTablePointerUp(row: TableRow) {
    const drag = dragState.current;
    dragState.current = null;
    if (!drag || drag.id !== row.id) return;
    setSelectedId(row.id);
    if (drag.moved) {
      const current = rows.find((r) => r.id === row.id);
      if (current) {
        void patchTable(row.id, { pos_x: current.pos_x, pos_y: current.pos_y });
      }
    }
  }

  // ------------------------------------------------------------------ render

  const pct = (v: number) => `${(v / FLOOR_GRID_SIZE) * 100}%`;

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('floorPlan.title')}
        subtitle={t('floorPlan.subtitle')}
        actions={
          <button className="btn" onClick={() => void addTable()} disabled={saving || loading}>
            + {t('floorPlan.addTable')}
          </button>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="panel">
        <div className="floor-editor-toolbar">
          <div className="floor-editor-rooms">
            {rooms.map((room) => (
              <button
                key={room}
                type="button"
                className={`floor-editor-room${room === effectiveRoom ? ' active' : ''}`}
                onClick={() => {
                  setActiveRoom(room);
                  setSelectedId(null);
                }}
              >
                {room}
              </button>
            ))}
            <button type="button" onClick={addRoom} title={t('floorPlan.addRoom')}>
              + {t('floorPlan.addRoom')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <div className="floor-editor-layout">
            <div
              className="floor-editor-canvas"
              ref={canvasRef}
              onPointerDown={(e) => {
                if (e.target === e.currentTarget) setSelectedId(null);
              }}
            >
              {roomTables.map((row) => (
                <div
                  key={row.id}
                  role="button"
                  className={`floor-editor-table ${row.shape}${row.id === selectedId ? ' selected' : ''}${row.active ? '' : ' inactive'}`}
                  style={{
                    left: pct(Number(row.pos_x)),
                    top: pct(Number(row.pos_y)),
                    width: pct(Number(row.width)),
                    height: pct(Number(row.height)),
                  }}
                  onPointerDown={(e) => onTablePointerDown(e, row)}
                  onPointerMove={onTablePointerMove}
                  onPointerUp={() => onTablePointerUp(row)}
                >
                  <span className="floor-editor-table-name">{row.name}</span>
                  <span className="floor-editor-table-seats">{row.seats} <Users className="ui-i" /></span>
                </div>
              ))}
              {roomTables.length === 0 && (
                <div className="floor-editor-canvas-empty text-muted">
                  {t('floorPlan.emptyRoom')}
                </div>
              )}
            </div>

            <aside className="floor-editor-panel">
              {!selected ? (
                <p className="text-muted" style={{ fontSize: 13 }}>
                  {t('floorPlan.selectHint')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label className="form-field">
                    <span className="form-label">{t('floorPlan.tableName')}</span>
                    <input
                      value={selected.name}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((r) =>
                            r.id === selected.id ? { ...r, name: e.target.value } : r,
                          ),
                        )
                      }
                      onBlur={(e) => {
                        const name = e.target.value.trim();
                        if (name) void patchTable(selected.id, { name });
                      }}
                    />
                  </label>

                  <label className="form-field">
                    <span className="form-label">{t('floorPlan.seats')}</span>
                    <input
                      type="number"
                      min={1}
                      value={selected.seats}
                      onChange={(e) =>
                        void patchTable(selected.id, {
                          seats: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                    />
                  </label>

                  <div className="form-field">
                    <span className="form-label">{t('floorPlan.shape')}</span>
                    <div className="floor-editor-shape-row" style={{ display: 'flex', gap: 6 }}>
                      {(['square', 'round', 'rect'] as const).map((shape) => (
                        <button
                          key={shape}
                          type="button"
                          className={selected.shape === shape ? 'active' : ''}
                          onClick={() =>
                            void patchTable(selected.id, {
                              shape,
                              ...(shape === 'rect'
                                ? { width: Math.max(Number(selected.width), 18) }
                                : { width: selected.height }),
                            })
                          }
                        >
                          {t(`floorPlan.shape_${shape}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-field">
                    <span className="form-label">{t('floorPlan.size')}</span>
                    <div
                      className="floor-editor-size-row"
                      style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          const w = Math.max(MIN_SIZE, Number(selected.width) - 2);
                          const h = Math.max(MIN_SIZE, Number(selected.height) - 2);
                          void patchTable(selected.id, { width: w, height: h });
                        }}
                      >
                        −
                      </button>
                      <span style={{ fontSize: 13, minWidth: 52, textAlign: 'center' }}>
                        {Number(selected.width)}×{Number(selected.height)}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const w = Math.min(MAX_SIZE, Number(selected.width) + 2);
                          const h = Math.min(MAX_SIZE, Number(selected.height) + 2);
                          void patchTable(selected.id, {
                            width: w,
                            height: h,
                            pos_x: clampPos(Number(selected.pos_x), w),
                            pos_y: clampPos(Number(selected.pos_y), h),
                          });
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <label className="form-field">
                    <span className="form-label">{t('floorPlan.room')}</span>
                    <select
                      value={selected.room}
                      onChange={(e) => void patchTable(selected.id, { room: e.target.value })}
                    >
                      {rooms.map((room) => (
                        <option key={room} value={room}>
                          {room}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={selected.active}
                      onChange={(e) => void patchTable(selected.id, { active: e.target.checked })}
                    />
                    {t('floorPlan.active')}
                  </label>

                  <button
                    type="button"
                    className="btn-ghost danger floor-editor-delete-btn"
                    onClick={() => void deleteTable(selected.id)}
                  >
                    <Trash2 className="ui-i" /> {t('common.delete')}
                  </button>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}
